import { type ClipboardEventHandler, type KeyboardEventHandler, useMemo, useState } from 'react';
import { Badge, Icon, Input } from '@utilities-one/gpm-ui';

import { FormBase } from '@/library/form/components/FormBase';
import { useFieldContext } from '@/library/form/contexts';
import { FormControlProps } from '@/library/form/types/form.type';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const DELIMS_RE = /[,\s;]+/;

type FormEmailTokensProps = FormControlProps & {
  placeholder?: string;
  maxCount?: number;
};

/**
 * Email tag input on a `string[]` field (legacy `EmailTokensInput`, rebuilt on
 * gpm-ui per plan §8.3 — no `input-tag` export yet): type an address and
 * commit with Enter/Space/comma/semicolon (or paste a delimited list);
 * Backspace on an empty input removes the last token; each chip has a remove
 * button. Tokens are lowercased, validated, and de-duplicated on entry.
 */
export function FormEmailTokens({ placeholder = 'Add email…', maxCount, ...props }: FormEmailTokensProps) {
  const field = useFieldContext<string[]>();
  const [draft, setDraft] = useState('');
  const emails = useMemo(() => field.state.value ?? [], [field.state.value]);
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;

  const addTokens = (raw: string) => {
    const tokens = raw
      .split(DELIMS_RE)
      .map((token) => token.trim().toLowerCase())
      .filter((token) => token.length > 0 && EMAIL_RE.test(token));
    if (tokens.length === 0) return;

    const next = [...emails];
    for (const token of tokens) {
      if (next.includes(token)) continue;
      if (maxCount !== undefined && next.length >= maxCount) break;
      next.push(token);
    }
    if (next.length !== emails.length) field.handleChange(next);
    setDraft('');
  };

  const removeEmail = (email: string) => {
    field.handleChange(emails.filter((entry) => entry !== email));
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === ',' || event.key === ';') {
      event.preventDefault();
      if (draft.trim()) addTokens(draft);
      return;
    }
    if (event.key === 'Backspace' && draft.length === 0 && emails.length > 0) {
      event.preventDefault();
      field.handleChange(emails.slice(0, -1));
    }
  };

  const onPaste: ClipboardEventHandler<HTMLInputElement> = (event) => {
    const text = event.clipboardData.getData('text');
    if (DELIMS_RE.test(text)) {
      event.preventDefault();
      addTokens(text);
    }
  };

  return (
    <FormBase {...props}>
      <Input
        id={field.name}
        autoComplete="off"
        placeholder={placeholder}
        value={draft}
        aria-invalid={isInvalid}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => {
          if (draft.trim()) addTokens(draft);
          field.handleBlur();
        }}
      />
      {emails.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {emails.map((email) => (
            <Badge key={email} variant="secondary" className="gap-1 pr-1">
              {email}
              <button
                type="button"
                aria-label={`Remove ${email}`}
                className="hover:bg-muted-foreground/20 rounded-full p-0.5"
                onClick={() => removeEmail(email)}
              >
                <Icon name="x" className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </FormBase>
  );
}
